import { HttpParams } from '@angular/common/http'
import { Component } from '@angular/core'
import { FormsModule } from '@angular/forms'
import { FaIconComponent } from '@fortawesome/angular-fontawesome'
import { faSync } from '@fortawesome/free-solid-svg-icons'
import { WjGridModule } from '@grapecity/wijmo.angular2.grid'
import { WjInputModule } from '@grapecity/wijmo.angular2.input'
import { CellEditEndingEventArgs, DataMap, FlexGrid } from '@grapecity/wijmo.grid'

import { BaseComponent, FeatureType, Grid, JobPoUser, JobPoVendor, ListItem, ModalSize, Vendor, VpJob, VpUser } from '../../shared'
import { ActionToolbarCount } from '../../shared/reusable-components/action-toolbar-count/action-toolbar-count.component'
import { ActionToolbarSearchComponent } from '../../shared/reusable-components/action-toolbar-search/action-toolbar-search.component'
import { AutoSizeDirective } from '../../shared/utility/auto-size.directive'
import { JobPoAddUserComponent } from './job-po-add-user.component'
import { JobPoAddVendorComponent } from './job-po-add-vendor.component'

@Component({
    selector: 'app-job-po-configuration',
    templateUrl: './job-po-configuration.component.html',
    styleUrls: ['./job-po-configuration.component.css'],
    standalone: true,
    imports: [
        FormsModule,
        ActionToolbarCount,
        ActionToolbarSearchComponent,
        WjGridModule,
        WjInputModule,
        AutoSizeDirective,
        FaIconComponent,
    ],
})
export class JobPoConfigurationComponent extends BaseComponent {
    faSync = faSync

    vendorGrid: Grid<JobPoVendor>
    userGrid: Grid<JobPoUser>

    phaseMap!: DataMap

    activeJobs: VpJob[] = []
    jobMap: ListItem<string>[] = []
    vendors: Vendor[] = []
    activeUsers: VpUser[] = []

    selectedJob = ''
    selectedJobType = ''

    jobVendors: JobPoVendor[] = []
    jobUsers: JobPoUser[] = []

    isLoadingConfig = false
    isInitializing = false
    isAddingFromViewpoint = false

    get canEdit() {
        return this.services.authentication.hasFeature([FeatureType.FieldToolsPoConfigEdit])
    }

    constructor() {
        super()
        this.vendorGrid = new Grid<JobPoVendor>(this.services)
            .wrapColumnHeaders()
            .filterBy(item => [item.vendorName])
            .isReadOnly(false)

        this.userGrid = new Grid<JobPoUser>(this.services)
            .wrapColumnHeaders()
            .isReadOnly(true)
            .filterBy(item => [item.userName])

        this.refresh()
    }

    refresh() {
        this.cancelableRequest(
            '',
            true,
            this.services.data
                .load(
                    true,
                    true,
                    this.services.data.vpJobs.queryAll(),
                    this.services.data.vendors.queryAll(),
                    this.services.data.get<VpUser[]>('api/VpUser/GetVistaUsers', new HttpParams().set('activeOnly', true)),
                    this.services.data.get<{ value: string; display: string }[]>('api/JobPoConfiguration/GetPhasesForJobPoConfig'),
                )
                .subscribe({
                    next: result => {
                        this.activeJobs = result[0] as VpJob[]
                        this.jobMap = this.activeJobs.map(x => new ListItem(x.id, x.id + ' - ' + x.name)).orderBy(x => x.display)
                        this.vendors = result[1] as Vendor[]
                        this.activeUsers = result[2] as VpUser[]

                        const phases = result[3] as { value: string; display: string }[]
                        const phaseListItems: ListItem<string | null>[] = [new ListItem<string | null>(null, '')]
                        phases.forEach(x => phaseListItems.push(new ListItem<string | null>(x.value, x.display)))
                        this.phaseMap = new DataMap(phaseListItems, 'value', 'display')

                        this.initialLoadingComplete()
                    },
                }),
        )
    }

    onJobChanged() {
        if (!this.selectedJob) {
            this.selectedJobType = ''
            this.jobVendors = []
            this.jobUsers = []
            this.vendorGrid.items = []
            this.userGrid.items = []
            return
        }

        // Find the job type from the selected job
        const job = this.activeJobs.find(x => x.id === this.selectedJob)
        this.selectedJobType = job?.jobType || '(Not assigned)'

        this.loadJobConfiguration()
    }

    loadJobConfiguration() {
        this.isLoadingConfig = true

        this.services.data
            .load(
                true,
                true,
                this.services.data.get<JobPoVendor[]>(
                    'api/JobPoConfiguration/GetVendorsByJob',
                    new HttpParams().set('job', this.selectedJob),
                ),
                this.services.data.get<JobPoUser[]>('api/JobPoConfiguration/GetUsersByJob', new HttpParams().set('job', this.selectedJob)),
            )
            .subscribe({
                next: result => {
                    this.jobVendors = result[0]
                    this.jobUsers = result[1]
                    this.vendorGrid.items = this.jobVendors
                    this.userGrid.items = this.jobUsers
                    this.isLoadingConfig = false
                },
            })
    }

    initializeFromTemplate() {
        if (!this.selectedJob) return

        const hasExisting = this.jobVendors.length > 0
        if (hasExisting) {
            if (
                !confirm(
                    'This job already has vendors configured. Initialize from template will add any missing template vendors. Continue?',
                )
            ) {
                return
            }
        }

        this.isInitializing = true
        this.services.data
            .post<JobPoVendor[]>('api/JobPoConfiguration/InitializeFromTemplate', {}, new HttpParams().set('job', this.selectedJob))
            .subscribe({
                next: () => {
                    this.isInitializing = false
                    this.services.notification.success('Vendors initialized from template.')
                    this.loadJobConfiguration()
                },
                error: error => {
                    this.isInitializing = false
                    const message = error?.error || 'Failed to initialize from template.'
                    this.services.notification.error(message)
                },
            })
    }

    addUsersFromViewpoint() {
        if (!this.selectedJob) return

        this.isAddingFromViewpoint = true
        this.services.data
            .post<JobPoUser[]>('api/JobPoConfiguration/AddUsersFromViewpoint', {}, new HttpParams().set('job', this.selectedJob))
            .subscribe({
                next: result => {
                    this.jobUsers = result
                    this.userGrid.items = this.jobUsers
                    this.isAddingFromViewpoint = false
                    this.services.notification.success('Users added from Viewpoint.')
                },
                error: error => {
                    this.isAddingFromViewpoint = false
                    const message = error?.error || 'Failed to add users from Viewpoint.'
                    this.services.notification.error(message)
                },
            })
    }

    manageVendors() {
        if (!this.selectedJob) return

        const ref = this.services.modals.open(JobPoAddVendorComponent, ModalSize.large)
        const modal = ref.componentInstance as JobPoAddVendorComponent
        modal.job = this.selectedJob
        modal.allVendors = this.vendors.map(v => ({ id: v.id, name: v.name })).sort((a, b) => a.name.localeCompare(b.name))
        modal.selectedVendorIds = this.jobVendors.map(v => v.vendorId)

        ref.result
            .then((result: JobPoVendor[]) => {
                if (result) {
                    this.jobVendors = result
                    this.vendorGrid.items = this.jobVendors
                }
            })
            // eslint-disable-next-line @typescript-eslint/no-empty-function
            .catch(() => {})
    }

    manageUsers() {
        if (!this.selectedJob) return

        const ref = this.services.modals.open(JobPoAddUserComponent, ModalSize.large)
        const modal = ref.componentInstance as JobPoAddUserComponent
        modal.job = this.selectedJob
        modal.allUsers = this.activeUsers.map(u => ({ id: u.id, name: u.name })).sort((a, b) => a.name.localeCompare(b.name))
        modal.selectedUserHrRefs = this.jobUsers.map(u => u.userHrRef)

        ref.result
            .then((result: JobPoUser[]) => {
                if (result) {
                    this.jobUsers = result
                    this.userGrid.items = this.jobUsers
                }
            })
            // eslint-disable-next-line @typescript-eslint/no-empty-function
            .catch(() => {})
    }

    onVendorCellEditEnded(flexGrid: FlexGrid, args: CellEditEndingEventArgs) {
        if (args.cancel) return

        const col = flexGrid.columns[args.col]
        if (col.binding !== 'phase') return

        const item = flexGrid.rows[args.row].dataItem as JobPoVendor
        const phase = item.phase || ''

        this.services.data
            .put<JobPoVendor>('api/JobPoConfiguration/UpdateVendorPhase', {}, new HttpParams().set('id', item.id).set('phase', phase))
            .subscribe({
                error: () => {
                    this.services.notification.error('Failed to save phase change.')
                    this.loadJobConfiguration()
                },
            })
    }
}
